/**
 * What the `/auth/reset` screen makes of a reset link and of Firebase's
 * answers to it. Kept out of the screen so it can be tested (this repo's jest
 * config stubs `react-native`).
 */

/** Firebase refuses anything shorter (`auth/weak-password`). */
export const MIN_PASSWORD_LENGTH = 6;

export type ResetLinkErrorKind = 'invalid' | 'weak-password' | 'network' | 'unknown';

/**
 * `invalid` covers every way a link can be dead: expired (one hour), already
 * used, mistyped, or for an account that is gone or disabled. The cook can do
 * the same thing about all of them, which is ask for a new one.
 */
export function resetLinkErrorKind(error: unknown): ResetLinkErrorKind {
  const code = (error as { code?: unknown })?.code;
  switch (code) {
    case 'auth/expired-action-code':
    case 'auth/invalid-action-code':
    case 'auth/user-disabled':
    case 'auth/user-not-found':
      return 'invalid';
    case 'auth/weak-password':
      return 'weak-password';
    case 'auth/network-request-failed':
      return 'network';
    default:
      return 'unknown';
  }
}

/** The `oobCode` query parameter, or null when the link carries none. */
export function readResetCode(param: string | string[] | undefined): string | null {
  const value = Array.isArray(param) ? param[0] : param;
  const code = typeof value === 'string' ? value.trim() : '';
  return code.length > 0 ? code : null;
}

/**
 * Where `public/index.html` leaves the code it took out of the address bar.
 * That inline script runs before the bundle, so expo-router, posthog-js
 * (which saves the entry URL in its own cookie, beyond `before_send`'s reach)
 * and the session recorder never see the code at all.
 */
export const RESET_CODE_HANDOFF_KEY = '__duchefResetCode';

let handedOff: string | null | undefined;

/**
 * The code the page script took, if any. Read once and kept, because a
 * StrictMode render calls a state initialiser twice.
 */
export function takeHandedOffResetCode(): string | null {
  if (handedOff !== undefined) return handedOff;
  if (typeof window === 'undefined') return null;
  const holder = window as unknown as Record<string, unknown>;
  const value = holder[RESET_CODE_HANDOFF_KEY];
  delete holder[RESET_CODE_HANDOFF_KEY];
  handedOff = readResetCode(typeof value === 'string' ? value : undefined);
  return handedOff;
}

/** Test seam. */
export function resetHandedOffResetCode(): void {
  handedOff = undefined;
}
